import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Sparkles, ArrowRight, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone-100 flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-sm" data-testid="forgot-password-page">
        <Link to="/" className="flex items-center gap-2 mb-12">
          <Sparkles size={20} strokeWidth={1.5} className="text-forest-600" />
          <span className="font-heading text-2xl text-forest-700">Toothpix</span>
        </Link>

        {sent ? (
          <div data-testid="forgot-password-sent">
            <MailCheck size={28} strokeWidth={1.4} className="text-forest-600" />
            <h1 className="font-heading text-4xl text-forest-700 mt-6">Check your inbox</h1>
            <p className="text-sm text-forest-400 mt-4 leading-relaxed">
              If an account exists for <b className="text-forest-700">{email.trim()}</b>, we've sent a
              password reset link. It expires in 1 hour.
            </p>
            <p className="text-sm text-forest-400 mt-3 leading-relaxed">
              Didn't get it? Check your spam folder, or{" "}
              <button
                onClick={() => setSent(false)}
                className="text-forest-600 underline underline-offset-4"
                data-testid="forgot-try-again"
              >
                try again
              </button>
              .
            </p>
            <Link
              to="/login"
              className="btn-secondary w-full justify-center mt-8"
              data-testid="forgot-back-to-login"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-password-form">
            <div className="overline mb-3">Password reset</div>
            <h1 className="font-heading text-4xl text-forest-700">Forgot password?</h1>
            <p className="text-sm text-forest-400 mt-2">
              Enter your clinic email and we'll send you a link to reset your password.
            </p>

            <div className="mt-10 space-y-5">
              <div>
                <label className="label-flat">Email</label>
                <input
                  type="email"
                  className="input-flat"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-email"
                />
              </div>

              {err && (
                <div className="text-sm text-clay-600 border border-clay-600/30 bg-clay-400/10 px-3 py-2" data-testid="forgot-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center"
                data-testid="forgot-submit"
              >
                {loading ? "Sending…" : (<>Send reset link <ArrowRight size={14} /></>)}
              </button>

              <Link
                to="/login"
                className="block text-center text-sm text-forest-600 underline underline-offset-4"
                data-testid="forgot-back-link"
              >
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
